import { normalizeWisResponse } from 'wsm-wis-client';
import { geoLocationObj } from '../global-constants';

/**
 * Shape a raw WIS `getInventoryAndFacets` response into the client-facing
 * `{ facetResponse, inventoryResponse }` pair. This is the single source of truth
 * for WIS response shaping across the two client paths — SSR hydration
 * (`InvDataRefactored._hydrateFromSSR`) and the client fallback fetch
 * (`WISutils.requestInventoryAndFacets`) — so they can no longer drift (issue #341;
 * the geo-facet gate divergence found in PR #323 came from exactly this
 * duplication).
 *
 * Field mapping + deprecated-facet removal + `totalCount` derivation are delegated
 * to the shared `normalizeWisResponse` from wsm-wis-client. On top of that:
 *
 * - `geoEnabled` gates the location facet: when true, `geoLocationObj` is prepended
 *   to the facets array (mirrors the historic `if (geoData)` gate that once
 *   diverged between the two paths).
 * - Facets / inventory that are ABSENT in the raw response are left `undefined`
 *   (not coerced to `[]`), so callers keep their "missing data" branches — e.g.
 *   `WISutils` sets `emptyFacetObj`/`emptyInventoryObj` and gates tracking on
 *   presence.
 *
 * @param {Object} response - raw WIS response
 * @param {Object} [opts]
 * @param {boolean} [opts.geoEnabled] - prepend the location facet when true
 * @returns {{ facetResponse: Object, inventoryResponse: Object }}
 */
export const processWISResponse = (
	response = {},
	{ geoEnabled = false } = {}
) => {
	const normalized = normalizeWisResponse(response);

	// Preserve "facets absent" as undefined (normalizeWisResponse coerces to []);
	// callers branch on this. When present, apply the geo-facet gate.
	let facets;
	if (Array.isArray(response.facets)) {
		facets = geoEnabled
			? [geoLocationObj, ...normalized.facets]
			: normalized.facets;
	}

	return {
		facetResponse: {
			facets,
			filters: normalized.filters,
			offSiteData: normalized.offSiteData,
			suggestedFilters: normalized.suggestedFilters,
			totalCount: normalized.totalCount
		},
		inventoryResponse: {
			accounts: normalized.accounts,
			incentives: normalized.incentives,
			inventory: normalized.inventory,
			pageInfo: normalized.pageInfo
		}
	};
};
